/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/metadata/sequence
 * @desc Column Definition for metadata sequence
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import { SequenceEditor } from 'labstep-web/core/SequenceEditor';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';
import { generateNewDateString } from 'labstep-web/services/date.service';

export const getMetadataSequenceColDef = (
  experimentOrProtocol: Experiment | Protocol,
  parentEntityClass: string,
  metadataType: MetadataType,
  metadataLabel: string,
  protocolValueName?: string,
): SmartTableColDef => {
  return {
    colId: protocolValueName
      ? `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:${protocolValueName}`
      : `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      return {
        icon: protocolValueName
          ? ICONS.metadata.secondary
          : ICONS.metadata.type.sequence,
        cornerIcon:
          protocolValueName &&
          (parentEntityClass === Resource.entityName
            ? ICONS.resource.primary
            : ICONS.resource_item.primary),
        secondaryHeader:
          protocolValueName &&
          `${protocolValueName}${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}`,
        toolPanelColumnName: `${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}${metadataLabel}`,
        experimentOrProtocol,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolValueService.getMetadataEditable(
        params,
        experimentOrProtocol,
        metadataType,
        metadataLabel,
        parentEntityClass,
        protocolValueName,
      ),
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return null;
      }

      return metadata?.sequence?.sequenceData?.sequence;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }
      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return (
          <TextPlaceholder
            onClick={() => {
              return GridSmartTableProtocolValueService.createMetadata(
                params,
                experimentOrProtocol,
                parentEntityClass,
                metadataType,
                metadataLabel,
                protocolValueName,
                {
                  sequence: { name: 'Untitled sequence' },
                },
              );
            }}
          >
            Create sequence
          </TextPlaceholder>
        );
      }

      if (!metadata?.sequence?.sequenceData?.sequence) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridSmartTableProtocolValueService.getMetadataEditable(
              params,
              experimentOrProtocol,
              metadataType,
              metadataLabel,
              parentEntityClass,
              protocolValueName,
            )}
            children={'Edit sequence'}
            placeholder={
              GridSmartTableProtocolValueService.getMetadataEditable(
                params,
                experimentOrProtocol,
                metadataType,
                metadataLabel,
                parentEntityClass,
                protocolValueName,
              )
                ? 'Edit sequence'
                : ' '
            }
          />
        );
      }
      return metadata.sequence.sequenceData.sequence;
    },
    cellEditorPopup: true,
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return '';
      }

      return (
        <ReadOnMountHOC
          type="entities"
          entityName={Metadata.entityName}
          params={{
            id: metadata.idAttr,
            get_single: 1,
          }}
          loading={{ loader: 'placeholder' }}
          children={({ entity: metadata }) => {
            return (
              <SequenceEditor
                sequenceData={metadata.sequence.sequenceData}
                onClose={(): void => {
                  stopEditingAndFocus(params);
                }}
                readOnly={false}
                onSave={(sequenceData) => {
                  const { name, ...data } = sequenceData;
                  const newValue = {
                    name,
                    data: JSON.stringify(data),
                  };

                  GridSmartTableProtocolValueService.updateEntity(
                    params,
                    metadata.entityName,
                    metadata.guid,
                    {
                      sequence: newValue,
                    },
                  );
                }}
              />
            );
          }}
        />
      );
    },
    cellStyle: (params) =>
      GridSmartTableProtocolValueService.getCellStyle(
        params,
        experimentOrProtocol,
        Metadata.entityName,
        metadataLabel,
        parentEntityClass,
      ),
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: (params: any) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (metadata) {
        return GridSmartTableProtocolValueService.updateEntity(
          params,
          metadata.entityName,
          metadata.guid,
          {
            deleted_at: generateNewDateString(),
          },
        );
      }
      return null;
    },
  };
};
